import { useRef, useState } from 'react';
import { useForm, UseFormReturn } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate } from 'react-router-dom';
import { documentTemplateEditPath } from '@dealstack/frontend/app/pages/DocumentTemplates/ManageDocumentTemplates.routes';
import { useCreateDocumentTemplate } from '@dealstack/frontend/app/pages/DocumentTemplates/api/useCreateDocumentTemplate';
import { useUploadDocumentTemplateFile } from '@dealstack/frontend/app/pages/DocumentTemplates/api/useUploadDocumentTemplateFile';
import { useTranslationDefault } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { useDealstackFeatureFlag } from '@dealstack/frontend/shared/hooks/useDealstackFeatureFlag/useDealstackFeatureFlag.hook';
import { FEATURE_FLAG } from '@dealstack/shared/lib/common/featureFlag';
import {
	promisedUploadFormat,
	renamedUploadKind,
	sniffUploadFormat,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/MasterDocuments/components/FileUpload/FileUpload.utils';

export enum CREATE_DOCUMENT_TEMPLATE_FORM_FIELDS {
	NAME = 'name',
	DESCRIPTION = 'description',
}

export const formShape = z.object({
	[CREATE_DOCUMENT_TEMPLATE_FORM_FIELDS.NAME]: z.string().nonempty(),
	[CREATE_DOCUMENT_TEMPLATE_FORM_FIELDS.DESCRIPTION]: z.string().optional(),
});

type CreateDocumentTemplateFormValues = z.infer<typeof formShape>;
export type CreateDocumentTemplateForm = UseFormReturn<CreateDocumentTemplateFormValues>;

export const useCreateDocumentTemplateForm = () => {
	const navigate = useNavigate();
	const tDefault = useTranslationDefault();
	const createDocumentTemplateRequest = useCreateDocumentTemplate();
	const uploadDocumentTemplateFileRequest = useUploadDocumentTemplateFile();

	// No deal or organisation to target: a library template is global, so the flag resolves for
	// the acting super-admin alone — the same evaluation the create endpoint makes.
	const { value: isDocxEnabled } = useDealstackFeatureFlag(FEATURE_FLAG.DOCX_MASTER_DOCUMENTS);

	// The source file is stored as-is and only its fileId is sent with the template.
	const [pdfFile, setPdfFile] = useState<File | null>(null);
	// Which pick is current, so an earlier sniff resolving late cannot overwrite a later one.
	const latestFile = useRef<File | null>(null);
	const [isCheckingFile, setIsCheckingFile] = useState(false);
	const [isUploading, setIsUploading] = useState(false);
	const [uploadError, setUploadError] = useState<string | null>(null);

	const createDocumentTemplateForm = useForm<CreateDocumentTemplateFormValues>({
		mode: 'all',
		resolver: zodResolver(formShape),
		defaultValues: {
			[CREATE_DOCUMENT_TEMPLATE_FORM_FIELDS.NAME]: '',
			[CREATE_DOCUMENT_TEMPLATE_FORM_FIELDS.DESCRIPTION]: '',
		},
	});

	/**
	 * Refuses a file whose extension its bytes contradict, before upload. `file.type` cannot
	 * see this — browsers derive it from the extension.
	 */
	const onSelectPdf = async (file: File | null) => {
		if (file === null) {
			latestFile.current = null;
			setPdfFile(null);
			setUploadError(null);
			return;
		}

		latestFile.current = file;
		// Shown immediately: the input is controlled, so waiting for the sniff left the field
		// blank while the author looked at the file they had just chosen.
		setPdfFile(file);
		setUploadError(null);
		setIsCheckingFile(true);

		let renamed: ReturnType<typeof renamedUploadKind>;
		try {
			renamed = renamedUploadKind(
				promisedUploadFormat(file.name),
				await sniffUploadFormat(file)
			);
		} finally {
			setIsCheckingFile(false);
		}

		// Two quick picks resolve in arbitrary order, so a slow sniff of an abandoned file must
		// not clear the one now on screen, or blame it for the other's mismatch.
		if (latestFile.current !== file) {
			return;
		}
		if (renamed !== null) {
			setPdfFile(null);
			setUploadError(
				renamed === 'pdf_named_docx'
					? tDefault('document_templates.create.error.pdf_named_docx')
					: tDefault('document_templates.create.error.docx_named_pdf')
			);
			return;
		}
		// Prefill the name from the filename when the user hasn't typed one.
		if (!createDocumentTemplateForm.getValues(CREATE_DOCUMENT_TEMPLATE_FORM_FIELDS.NAME)) {
			createDocumentTemplateForm.setValue(
				CREATE_DOCUMENT_TEMPLATE_FORM_FIELDS.NAME,
				file.name.replace(/\.(pdf|docx)$/i, ''),
				{ shouldValidate: true }
			);
		}
	};

	const onSubmit = async (formValues: CreateDocumentTemplateFormValues) => {
		if (!pdfFile) {
			setUploadError(tDefault('document_templates.create.error.file_required'));
			return;
		}
		setIsUploading(true);
		setUploadError(null);
		try {
			const uploaded = await uploadDocumentTemplateFileRequest.mutateAsync(pdfFile);
			// awaited, not fire-and-forget: `mutate` would let a create failure escape this
			// catch entirely and clear the uploading flag while the request was still running.
			const created = await createDocumentTemplateRequest.mutateAsync({
				name: formValues.name,
				description: formValues.description,
				fileId: uploaded.id,
			});
			navigate(documentTemplateEditPath(created.id));
		} catch {
			// The mutation's own onError already toasts; this also surfaces an inline
			// error on the field.
			setUploadError(tDefault('document_templates.create.error.upload_failed'));
		} finally {
			setIsUploading(false);
		}
	};

	return {
		navigate,
		createDocumentTemplateForm,
		onSubmit,
		pdfFile,
		onSelectPdf,
		isDocxEnabled,
		uploadError,
		isFormSubmitting:
			isCheckingFile ||
			isUploading ||
			createDocumentTemplateForm.formState.isSubmitting ||
			createDocumentTemplateRequest.isPending ||
			uploadDocumentTemplateFileRequest.isPending,
	};
};
